import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";

import { Constants, DOG_OWNER_PREMIUM_ENABLED } from "@eagerdog/constants";
import { IDog, IUser, IClub, /*UserUpdateDto, */IUserTransactionSummary } from "@eagerdog/interfaces";

import { helperService } from "src/services/helper.service";
import { localStorageService } from "src/services/local-storage.service";

import Confirm from "src/components/Confirm/Confirm";
import TabContent, { ITab, useTabContent } from "../../components/TabContent/TabContent";
import SectionSidebar, { ISectionDefinition, SectionMenuButton, sectionsFromTabs } from "src/components/SectionSidebar/SectionSidebar";
import { toast } from "../../components/Toast/ToastManager";
import { apiService } from "src/services/api.service";
import { AxiosError } from "axios";
import { Modal, useModal } from "src/components/Modal/Modal";
import DogForm, { DOG_FORM_IMAGE_TAB } from "src/components/StepForm/DogForm/DogForm";
import Congrats from "src/components/Congrats/Congrats";
import Input from "src/components/Input/Input";
import Checkbox from "src/components/Checkbox/Checkbox";
import keyIcon from "../../assets/icons/key.svg";
import Button from "src/components/Button/Button";
import Dropdown, { IOption, States, Provinces } from "src/components/Dropdown/Dropdown";
import Phone from "src/components/Phone/Phone";
import { download } from "src/components/Download/DownloadManager";
import DogTiles from "./DogTiles/DogTiles";

import UserWaitlistTab from "./UserWaitlistTab/UserWaitlistTab";
import UserScoresTab from "./UserScoresTab";
import MyClubsTab from "./MyClubsTab";
import UserTransactionsTab from "./components/UserTransactionsTab/UserTransactionsTab";
import { UserSubscriptionTab } from "./UserSubscriptionTab";
import ClubSettings from "../../components/ClubSettings/ClubSettings";
import GoogleMapComponent from "../../routes/KennelClub/components/GoogleMapComponent/GoogleMapComponent";

import flagIcon from "src/assets/icons/flag.svg";

import "../../styles/ManagePage.scss";
import "./Account.scss";

const OWNER_SECTIONS: ISectionDefinition[] = [
  { name: "My Dogs", group: "Activity", icon: "paw" },
  { name: "My Clubs", group: "Activity", icon: "registrations" },
  { name: "Entries", group: "Activity", icon: "ticket" },
  { name: "Results", group: "Activity", icon: "results" },
  { name: "Transactions", group: "Activity", icon: "transactions" },
  { name: "Personal Information", group: "Account", icon: "user" },
  { name: "Email & Password", group: "Account", icon: "settings" },
  { name: "Payment Methods", group: "Account", icon: "transactions" },
  { name: "Advanced", group: "Account", icon: "advanced" },
];

const MANAGER_SECTIONS: ISectionDefinition[] = [
  { name: "Basic Information", group: "Club", icon: "user" },
  { name: "Location", group: "Club", icon: "location" },
  { name: "Customization", group: "Club", icon: "image" },
  { name: "Account", group: "Account", icon: "settings" },
];

interface IProps { }

const Account: React.FC<IProps> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState<IUser>(helperService.getUser());
  const pUserType = helperService.usePrevious(user.user_type);

  const [tabs, setTabs] = useState<ITab[]>();
  const [loaded, setLoaded] = useState<boolean>(false);
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState<boolean>(false);
  const [savingPointsReminder, setSavingPointsReminder] = useState<boolean>(false);

  // Check for welcome parameter and show modal
  useEffect(() => {
    const queryParameters = new URLSearchParams(location.search);
    const welcome = queryParameters.get("welcome");
    if (welcome === "true") {
      setShowWelcomeModal(true);
      // Clean up URL by removing welcome parameter
      const newUrl = new URLSearchParams(location.search);
      newUrl.delete("welcome");
      const newSearch = newUrl.toString();
      navigate(location.pathname + (newSearch ? `?${newSearch}` : ""), { replace: true });
    }
  }, [location, navigate]);

  // Refresh user data on mount to get the latest has_dog_profile_access status
  useEffect(() => {
    const refreshUserData = async () => {
      try {
        const freshUser = await apiService.whoAmI();
        localStorageService.setItem(localStorageService.USER, JSON.stringify(freshUser));
        setUser(freshUser);
      } catch (error) {
        console.error("Failed to refresh user data:", error);
      }
    };
    
    // Initial refresh
    refreshUserData();
    
    // Listen for subscription updates from other tabs/components
    const handleUserDataUpdate = () => {
      refreshUserData();
    };
    
    window.addEventListener('userDataUpdated', handleUserDataUpdate);
    
    return () => {
      window.removeEventListener('userDataUpdated', handleUserDataUpdate);
    };
  }, []);

  // Derive subscription status from user's has_dog_profile_access flag
  // Dog Owner Premium is retired: every owner has profile access (hides the upgrade banner / redirect).
  const hasActiveSubscription = !DOG_OWNER_PREMIUM_ENABLED || (user.has_dog_profile_access ?? false);

  const tranLimit: number = 10;
  const [tranSkip, setTranSkip] = useState<number>(0);
  const [userTransactions, setUserTransactions] = useState<IUserTransactionSummary[]>([]);

  // Dog Info
  const [dogs, setDogs] = useState<IDog[]>([]);
  const [currentDog, setCurrentDog] = useState<IDog>();
  const [showDogModal, setShowDogModal] = useState<boolean>(false);
  const [dogFormTab, setDogFormTab] = useState(0);

  // Personal Info
  const [userInfoChanged, setUserInfoChanged] = useState<boolean>(false);

  const [firstName, setFirstName] = useState<string>(user?.first_name ?? "");
  const [lastName, setLastName] = useState<string>(user?.last_name ?? "");

  const [displayName, setDisplayName] = useState<string>(user?.display_name ?? "");

  const [website, setWebsite] = useState<string>(user?.website ?? "");
  const [line1, setLine1] = useState<string>(user?.address?.line1 ? user.address.line1 : "");
  const [line2, setLine2] = useState<string>(user?.address?.line2 ? user.address.line2 : "");
  const [city, setCity] = useState<string>(user?.address?.city ? user.address.city : "");
  const [country, setCountry] = useState<IOption>(user?.address?.country ? { id: user?.address?.country, value: user?.address?.country } : { value: "Canada", id: "Canada" });
  const [state, setState] = useState<IOption>(user?.address?.state ? Provinces.filter((o) => o.id === user?.address?.state)[0] || States.filter((o) => o.id === user?.address?.state)[0] || Provinces[0] : Provinces[0]);
  const [postalCode, setPostalCode] = useState<string>(user?.address?.postal_code ? user?.address?.postal_code : "");
  const [clubID, setClubID] = useState<string>(user?.club_number ? user?.club_number : "");
  const [phone, setPhone] = useState<string>(user?.phone ? user?.phone : "");
  const [eContactName, setEContactName] = useState<string>(user?.emergency_contact_name ? user?.emergency_contact_name : "");
  const [ePhone, setEPhone] = useState<string>(user?.emergency_contact_phone ? user?.emergency_contact_phone : "");

  const [parentName, setParentName] = useState<string>(user?.parent_name ? user?.parent_name : "");
  const [juniorDob, setJuniorDob] = useState<string>(user?.junior_dob ? moment(user.junior_dob).format("YYYY-MM-DD") : "");
  const [akcJuniorNum, setAkcJuniorNum] = useState<string>(user?.junior_numbers?.filter((num: any) => { return num.name === "AKC" })[0]?.value || "");
  const [ukcJuniorNum, setUkcJuniorNum] = useState<string>(user?.junior_numbers?.filter((num: any) => { return num.name === "UKC" })[0]?.value || "");
  const [ckcJuniorNum, setCkcJuniorNum] = useState<string>(user?.junior_numbers?.filter((num: any) => { return num.name === "CKC" })[0]?.value || "");
  const [ckcMembershipNum, setCkcMembershipNum] = useState<string>(user?.membership_numbers?.filter((num: any) => { return num.name === "CKC" })[0]?.value || "");

  const [isUpdatingPersonalInfo, setIsUpdatingPersonalInfo] = useState<boolean>(false);

  // Delete Dog Modal
  const { isShown, toggle } = useModal();

  // Club Settings
  const [currentClub, setCurrentClub] = useState<IClub>();
  const [mapCoordinates] = useState<{ lat: number; lng: number } | undefined>();

  const handleConfirm = () => {
    if (currentDog && currentDog?._id) {
      apiService.updateDog(currentDog._id, { ...currentDog, is_archived: true }).then(() => {
        toast.show({
          title: "My Dogs",
          content: "You've successfully deleted " + currentDog.call_name,
          duration: 10000,
          type: "success"

        })
        getDogs();
      }).catch((error: AxiosError) => {
        toast.show({
          title: "My Dogs",
          content: "Unable to delete dog.",
          duration: 10000,
          errorDetails: error,
          type: "fail"
        });
      });
    }

    setCurrentDog(undefined);
    toggle();
  };

  const handleCancel = () => {
    setCurrentDog(undefined);
    toggle();
  };

  // Password
  const [password, setPassword] = useState<string>("");
  const [repeat, setRepeat] = useState<string>("");

  // Email Change
  const [newEmail, setNewEmail] = useState<string>("");
  const [emailVerificationCode, setEmailVerificationCode] = useState<string>("");
  const [isEmailChangeRequested, setIsEmailChangeRequested] = useState<boolean>(false);
  const [isRequestingEmailChange, setIsRequestingEmailChange] = useState<boolean>(false);
  const [isVerifyingEmailChange, setIsVerifyingEmailChange] = useState<boolean>(false);

  const getUserTransactions = useCallback((id: string, _limit: number, _skip: number) => {
    let query:any = {
      //query: {
        "$and": [{
          attribute_name: "status",
          attribute_value: {
              operator: "$eq",
              value: "success"
          }
        }]
      //}
    };

    apiService.getUserTransactions(id, {
      $limit: _limit,
      $skip: _skip,
      sort: [{
        attribute_name: "created_at",
        sort: "desc"
      }],
      query
    }).then((response) => {
      setUserTransactions(response);
    });
  }, []);

  const getDogs = useCallback(() => {
    apiService.getUserDogs(user._id).then((response) => {
      setDogs(response);
    }).catch((error: AxiosError) => {
      toast.show({
        title: "My Dogs",
        content: "Unable to get dogs.",
        duration: 10000,
        errorDetails: error,
        type: "fail"
      });
    });
  }, [user._id]);

  const getCurrentClub = useCallback(() => {
    if (user.user_type === Constants.user_type.club_manager) {
      apiService.getCurrentClub().then((response) => {
        setCurrentClub(response);
      }).catch((error: AxiosError) => {
        toast.show({
          title: "Get Club",
          content: "Unable to get club information.",
          duration: 10000,
          errorDetails: error,
          type: "fail"
        });
      });
    }
  }, [user]);

  const { activeTab, setTab } = useTabContent();

  // Listen for URL changes and update tab accordingly (only when coming from menu links with ?t= parameter)
  useEffect(() => {
    const queryParameters = new URLSearchParams(location.search);
    const t = queryParameters.get("t");
    if (t !== null) {
      const tabIndex = parseInt(t);
      if (!isNaN(tabIndex)) {
        setTab(tabIndex);
      }
    }
  }, [location.search, setTab]);

  // Update URL when activeTab changes (from user clicking tabs)
  useEffect(() => {
    const queryParameters = new URLSearchParams(window.location.search);
    const currentTab = queryParameters.get("t");
    
    // Only update URL if the tab has changed
    if (currentTab !== activeTab.toString()) {
      queryParameters.set("t", activeTab.toString());
      navigate(`/account?${queryParameters.toString()}`, { replace: true });
    }
  }, [activeTab, navigate]);

  const savePointsReminder = (enabled: boolean) => {
    setSavingPointsReminder(true);
    apiService.updateSelf({ show_points_reminder: enabled }).then((response) => {
      localStorageService.setItem(localStorageService.USER, JSON.stringify(response));
      setUser(response);
      toast.show({
        title: "Advanced",
        content: "Your points reminder setting was saved.",
        duration: 5000,
        type: "success"
      });
    }).catch((error: AxiosError) => {
      toast.show({
        title: "Advanced",
        content: "Couldn't save your points reminder setting.",
        duration: 8000,
        errorDetails: error,
        type: "fail"
      });
    }).finally(() => {
      setSavingPointsReminder(false);
    });
  };

  const renderAdvanced = () => {
    return (
      <div className="settingsTab">
        <div className="pointsReminderSetting">
          <Checkbox
            id="showPointsReminder"
            label="Show warning when points are available in my wallet"
            value={user.show_points_reminder !== false}
            disabled={savingPointsReminder}
            onChange={(e) => savePointsReminder(e.target.checked)}
          />
          <p className="settingHint">Checkout asks before you pay if this club has points you have not used.</p>
        </div>
      </div>
    );
  };

  const renderOwnerTabs = () => {
    const queryParameters = new URLSearchParams(window.location.search);
    const t = queryParameters.get("t");

    if (tabs) {
      return (<TabContent hideTabs setTab={setTab} activeTab={activeTab} defaultTab={t ? parseInt(t) : undefined} tabs={tabs}>
        {renderDogs()}
        {renderWaitlist()}
        {renderOwnerResults()}
        {renderMyClubs()}
        {renderTransactions()}
        {renderPersonalInfo()}
        {renderAccountSettings()}
        {renderSubscriptionPlan()}
        {renderAdvanced()}
      </TabContent>);
    } else {
      return (null);
    }
  }

  const renderWaitlist = () => {
    return <UserWaitlistTab user={user} />;
  }

  const renderManagerTabs = () => {
    const queryParameters = new URLSearchParams(window.location.search);
    const t = queryParameters.get("t");

    if (tabs) {
      return (<TabContent hideTabs setTab={setTab} activeTab={activeTab} defaultTab={t ? parseInt(t) : undefined} tabs={tabs}>
        {renderBasicInformation()}
        {renderLocation()}
        {renderCustomization()}
        {renderAccountSettings()}
      </TabContent>);
    } else {
      return (null);
    }
  }

  // const renderClubSettings = () => {
  //   if (!currentClub) {
  //     return (
  //       <div className="settingsTab">
  //         <div className="description">
  //           <div className="title">Club Settings</div>
  //           <div className="text">Loading club information...</div>
  //         </div>
  //       </div>
  //     );
  //   }

  //   return (
  //     <div className="settingsTab">
  //       <div className="description">
  //         <div className="title">Club Settings</div>
  //         <div className="text">Customize your club's public page with logo, cover image, description, and location.</div>
  //       </div>
  //       <div className="inputs">
  //         <ClubSettings 
  //           club={currentClub} 
  //           onClubUpdate={(updatedClub) => setCurrentClub(updatedClub)} 
  //         />
  //       </div>
  //     </div>
  //   );
  // }

  const updateUserInfo = () => {
    setIsUpdatingPersonalInfo(true);

    let updatedUser: any = {
      first_name: firstName,
      last_name: lastName,
      display_name: user.user_type === Constants.user_type.club_manager ? displayName : firstName+" "+lastName,
      address: {
        line1,
        line2,
        country: country.id,
        city,
        state: state.id,
        postal_code: postalCode
      },
      phone: phone,
      website: helperService.normalizeUrl(website) || "",
      emergency_contact_name: eContactName,
      emergency_contact_phone: ePhone

    };

    if (user.user_type === Constants.user_type.club_manager) {
      updatedUser["club_number"] = clubID;
    }

    if (user.is_junior) {
      updatedUser.junior_numbers = [];
      updatedUser.parent_name = parentName;
      if (juniorDob) {
        updatedUser.junior_dob = new Date(juniorDob);
      }

      if (akcJuniorNum.length > 0) {
        updatedUser.junior_numbers.push({ name: Constants.sanctioning_club.AKC, value: akcJuniorNum });
      }

      if (ukcJuniorNum.length > 0) {
        updatedUser.junior_numbers.push({ name: Constants.sanctioning_club.UKC, value: ukcJuniorNum });
      }

      if (ckcJuniorNum.length > 0) {
        updatedUser.junior_numbers.push({ name: Constants.sanctioning_club.CKC, value: ckcJuniorNum });
      }
    }

    if (user.user_type !== Constants.user_type.club_manager) {
      updatedUser.membership_numbers = (user.membership_numbers || []).filter((num: any) => num.name !== Constants.sanctioning_club.CKC);
      if (ckcMembershipNum.trim().length > 0) {
        updatedUser.membership_numbers.push({ name: Constants.sanctioning_club.CKC, value: ckcMembershipNum.trim() });
      }
    }

    apiService.updateSelf(updatedUser).then((response) => {
      toast.show({
        title: "Personal Information",
        content: "Successfully updated personal information.",
        duration: 10000,
        type: "success"
      });

      localStorageService.setItem(localStorageService.USER, JSON.stringify(response));
      setUserInfoChanged(false);
    }).catch((error: AxiosError) => {
      toast.show({
        title: "Personal Information",
        content: "Unable to update personal information.",
        duration: 10000,
        errorDetails: error,
        type: "fail"
      });
    }).finally(() => {
      setIsUpdatingPersonalInfo(false);
    });
  }

  const updateUserPassword = () => {
    apiService.updateSelfPassword({ new_password: password }).then((response) => {
      toast.show({
        title: "Password",
        content: "Successfully updated password.",
        duration: 10000,
        type: "success"
      });
      setPassword("");
      setRepeat("");
    }).catch((error: AxiosError) => {
      toast.show({
        title: "Password",
        content: "Unable to update password.",
        duration: 10000,
        type: "fail",
        errorDetails: error
      })
    });
  };

  const requestEmailChange = () => {
    if (!newEmail || newEmail.toLowerCase() === user.email.toLowerCase()) {
      toast.show({
        title: "Email Change",
        content: "Please enter a different email address.",
        duration: 5000,
        type: "fail"
      });
      return;
    }

    setIsRequestingEmailChange(true);
    apiService.emailChangeRequest(newEmail).then(() => {
      toast.show({
        title: "Email Change",
        content: "A verification code has been sent to your new email address.",
        duration: 10000,
        type: "success"
      });
      setIsEmailChangeRequested(true);
    }).catch((error: AxiosError) => {
      toast.show({
        title: "Email Change",
        content: "Unable to request email change.",
        duration: 10000,
        type: "fail",
        errorDetails: error
      });
    }).finally(() => {
      setIsRequestingEmailChange(false);
    });
  };

  const verifyEmailChange = () => {
    if (!emailVerificationCode) {
      toast.show({
        title: "Email Change",
        content: "Please enter the verification code.",
        duration: 5000,
        type: "fail"
      });
      return;
    }

    setIsVerifyingEmailChange(true);
    apiService.emailChangeVerify(emailVerificationCode).then((updatedUser) => {
      toast.show({
        title: "Email Change",
        content: "Your email has been successfully updated.",
        duration: 10000,
        type: "success"
      });
      // Update local storage with new user data
      localStorageService.setItem(localStorageService.USER, JSON.stringify(updatedUser));
      // Reset state
      setNewEmail("");
      setEmailVerificationCode("");
      setIsEmailChangeRequested(false);
      // Reload to reflect changes - preserve the email and password tab via URL param.
      // Owner tabs: My Dogs=0, Entries=1, Results=2, My Clubs=3, Transactions=4, Personal Information=5, Email & Password=6, Billing=7, Advanced=8
      // Club manager: Account is at index 3
      const accountTabIndex = user.user_type === Constants.user_type.club_manager ? 3 : 6;
      window.location.href = `/account?t=${accountTabIndex}`;
    }).catch((error: AxiosError<any>) => {
      const errorMessage = error.response?.data?.message || "The verification code is invalid or may have expired. Please request a new code.";
      toast.show({
        title: "Email Change",
        content: errorMessage,
        duration: 10000,
        type: "fail",
        errorDetails: error
      });
    }).finally(() => {
      setIsVerifyingEmailChange(false);
    });
  };

  const cancelEmailChange = () => {
    setNewEmail("");
    setEmailVerificationCode("");
    setIsEmailChangeRequested(false);
  };

  useEffect(() => {
    if (!helperService.isLoggedIn()) {
      toast.show({
        title: "Account",
        content: "Please login to access account information",
        duration: 10000,
        type: "fail"
      });

      navigate("/login?r=/account");
    }

    if (!loaded && user.user_type.length > 0 && (pUserType !== user.user_type) && helperService.isLoggedIn()) {
      switch (user.user_type) {
        case "owner":
          setTabs([
            { name: "My Dogs", actions: [] },
            { name: "Entries", actions: [] },
            { name: "Results", actions: [] },
            { name: "My Clubs", actions: [] },
            { name: "Transactions", actions: [] },
            { name: "Personal Information", actions: [] },
            { name: "Email & Password", actions: [] },
            { name: "Payment Methods", actions: [] },
            { name: "Advanced", actions: [] },
          ]);

          getDogs();
          break;
        case "club_manager":
          setTabs([
            { name: "Basic Information", actions: [] },
            { name: "Location", actions: [] },
            { name: "Customization", actions: [] },
            { name: "Account", actions: [] },
          ]);
          getCurrentClub();
          break;
      }

      getUserTransactions(user._id, tranLimit, tranSkip);

      setLoaded(true);
    }
  }, [loaded, pUserType, user.user_type, navigate, getUserTransactions, tranSkip, user._id, getDogs, getCurrentClub]);

  const renderPersonalInfo = () => {
    return (<>
      <div className="settingsTab personalInfo">
        {!user?.is_email_verified &&
          <div className="accountWarning">
            <div className="iconWrapWarning"><div className="icon alert"></div></div>
            <div className="details">
              <span>Your email isn't verified, this may hinder some functionality on Eagerdog. <Link to="/verify-email?r=/account&sendOnLoad=1">Verify Email</Link></span>
            </div>
          </div>
        }
        <form className="inputs" onSubmit={(e) => { e.preventDefault(); updateUserInfo(); }}>
          {user.user_type === Constants.user_type.club_manager && <div className="inputRow">
            <Input required label="Account Name" id="displayName" type="text" onChange={(e) => { e.stopPropagation(); setDisplayName(e.target.value); setUserInfoChanged(true); }} defaultValue={displayName} placeholder="Update your clubs name" />
          </div>}
          {user.user_type !== Constants.user_type.club_manager &&  <div className="inputRow">
            <Input label="First Name" id="firstName" type="text" onChange={(e) => { e.stopPropagation(); setFirstName(e.target.value); setUserInfoChanged(true); }} defaultValue={firstName} placeholder="Update your first name" />            
          </div>}
          {user.user_type !== Constants.user_type.club_manager && <div className="inputRow">
            <Input label="Last Name" id="lastName" type="text" onChange={(e) => { e.stopPropagation(); setLastName(e.target.value); setUserInfoChanged(true); }} defaultValue={lastName} placeholder="Update your last name" />            
          </div>}
          <div className="inputRow">
            <Phone label="Phone Number" value={phone} onChange={(e: any) => { setPhone(e); setUserInfoChanged(true); }} />
          </div>
          <div className="inputRow">            
            <div className={!user.is_email_verified ? "emailWrap unverified" : "emailWrap"}><Input disabled required label="Email" onChange={(e:any) => {  }} defaultValue={user?.parent_email ? user?.parent_email : user?.email} type="email" placeholder="Enter your email" />{!user?.is_email_verified && <div className="iconWrap"><div className="icon alert"></div></div>}</div>
          </div>
          <div className="inputRow">
            <Input label="Website" id="website" type="text" onChange={(e) => { e.stopPropagation(); setWebsite(e.target.value); setUserInfoChanged(true); }} defaultValue={user?.website} placeholder="Update your website" />            
          </div>
          <div className="inputRow">
          <Input label="Address Line 1" id="line1" type="text" onChange={(e) => { e.stopPropagation(); setLine1(e.target.value); setUserInfoChanged(true); }} defaultValue={line1} placeholder="Address Line 1" />
          </div>
          <div className="inputRow">
            <Input label="Line 2" id="line2" type="text" onChange={(e) => { e.stopPropagation(); setLine2(e.target.value); setUserInfoChanged(true); }} defaultValue={line2} placeholder="Line 2" />
          </div>
          <div className="inputRow">
            <Input label="City" id="city" type="text" onChange={(e) => { e.stopPropagation(); setCity(e.target.value); setUserInfoChanged(true); }} defaultValue={city} placeholder="City" /> 
          </div>
          <div className="inputRow">            
            <Dropdown value={state} onChange={(e: any, value: IOption) => { setState(value); setUserInfoChanged(true); }} icon={flagIcon} label="Province / State" options={country.value === "USA" ? States : Provinces} placeholder="Choose your state/province of residence" />
          </div>
          <div className="inputRow">
            <Dropdown value={country} onChange={(e: any, value: IOption) => { setCountry(value); }} icon={flagIcon} label="Country" options={[{ value: "Canada", id: "Canada" }, { value: "USA", id: "USA" }]} placeholder="Choose your country of residence" />
          </div>
          <div className="inputRow">
            <Input label="Postal/Zip Code" id="postalCode" type="text" onChange={(e) => { e.stopPropagation(); setPostalCode(e.target.value); setUserInfoChanged(true); }} defaultValue={postalCode} placeholder="Postal Code" />
          </div>
          {user?.is_junior && <>
            <div className="inputRow">
              <Input required label="Parents Name" id="parentName" type="text" onChange={(e) => { e.stopPropagation(); setUserInfoChanged(true); setParentName(e.target.value); }} defaultValue={parentName} placeholder="" />
            </div>
            <div className="inputRow">
              <Input required label="Date of Birth" id="juniorDob" type="date" onChange={(e) => { e.stopPropagation(); setUserInfoChanged(true); setJuniorDob(e.target.value); }} defaultValue={juniorDob} placeholder="MM/DD/YYYY" />
            </div>
            <div className="inputRow">
              <Input label="AKC Junior #" id="akcJuniorNum" type="text" onChange={(e) => { e.stopPropagation(); setUserInfoChanged(true); setAkcJuniorNum(e.target.value); }} defaultValue={akcJuniorNum} placeholder="" />
            </div>
            <div className="inputRow">
              <Input label="UKC Junior #" id="ukcJuniorNum" type="text" onChange={(e) => { e.stopPropagation(); setUserInfoChanged(true); setUkcJuniorNum(e.target.value); }} defaultValue={ukcJuniorNum} placeholder="" />
            </div>
            <div className="inputRow">
              <Input label="CKC Junior #" id="ckcJuniorNum" type="text" onChange={(e) => { e.stopPropagation(); setUserInfoChanged(true); setCkcJuniorNum(e.target.value); }} defaultValue={ckcJuniorNum} placeholder="" />
            </div>
          </>}
          {user.user_type !== Constants.user_type.club_manager && <div className="inputRow">
            <Input label="CKC Membership # (optional)" id="ckcMembershipNum" type="text" onChange={(e) => { e.stopPropagation(); setUserInfoChanged(true); setCkcMembershipNum(e.target.value); }} defaultValue={ckcMembershipNum} placeholder="" />
          </div>}
          {user.user_type !== Constants.user_type.club_manager && <div className="inputRow">
            <Input label="Emergency Contact Name" id="eContactName" type="text" onChange={(e) => { e.stopPropagation(); setEContactName(e.target.value); setUserInfoChanged(true); }} defaultValue={eContactName} placeholder="Emergency Contact Name" />
          </div>}
          {user.user_type !== Constants.user_type.club_manager && <div className="inputRow">
            <Phone label="Emergency Contact Phone Number" value={ePhone} onChange={(e: any) => { setEPhone(e); setUserInfoChanged(true); }} />
          </div>}
          {user.user_type === Constants.user_type.club_manager && <div className="inputRow">            
            <Input label="Club ID" id="clubID" type="text" onChange={(e) => { e.stopPropagation(); setClubID(e.target.value); setUserInfoChanged(true); }} defaultValue={clubID} placeholder="Club ID" />
          </div>}
          <div className="actions">
            <Button type="submit" disabled={!userInfoChanged} isLoading={isUpdatingPersonalInfo}>Save</Button>
          </div>
        </form>
      </div>
    </>);
  }

  const renderAccountSettings = () => {
    const passwordsMatch = password.length > 0 && repeat.length > 0 && password === repeat;
    return (
      <div className="accountSettingsStack">
        <div className="settingsTab">
          <div className="description">
            <div className="title">Change Email</div>
            <div className="text">Update the email address associated with your account.</div>
          </div>
          <div className="inputs">
            {!isEmailChangeRequested ? (
              <>
                <div className="inputRow">
                  <Input 
                    label="Current Email" 
                    type="email" 
                    disabled 
                    defaultValue={user.parent_email ? user.parent_email : user.email}
                    onChange={() => {}}
                  />
                </div>
                <div className="inputRow">
                  <Input 
                    label="New Email Address" 
                    type="email" 
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)} 
                    placeholder="Enter your new email address" 
                  />
                </div>
                <div className="actions">
                  <Button 
                    disabled={!newEmail || newEmail.toLowerCase() === user.email.toLowerCase()} 
                    onClick={requestEmailChange} 
                    isLoading={isRequestingEmailChange}
                  >
                    Send Verification Code
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="inputRow">
                  <div className="emailChangeInfo">
                    A verification code has been sent to <strong>{newEmail}</strong>. Please check your inbox and enter the code below.
                  </div>
                </div>
                <div className="inputRow">
                  <Input 
                    label="Verification Code" 
                    type="text" 
                    value={emailVerificationCode}
                    onChange={(e) => setEmailVerificationCode(e.target.value)} 
                    placeholder="Enter the verification code" 
                  />
                </div>
                <div className="actions">
                  <Button 
                    className="secondary"
                    onClick={cancelEmailChange}
                  >
                    Cancel
                  </Button>
                  <Button 
                    disabled={!emailVerificationCode} 
                    onClick={verifyEmailChange} 
                    isLoading={isVerifyingEmailChange}
                  >
                    Verify & Update Email
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="settingsTab">
          <div className="description">
            <div className="title">Change Password</div>
            <div className="text">Update your password and keep your account safe.</div>
          </div>
          <div className="inputs">
            <Input onChange={(e) => { setPassword(e.target.value); }} value={password} required type="password" icon={keyIcon} label="New Password" placeholder="Enter your new password" />
            <Input onChange={(e) => { setRepeat(e.target.value); }} value={repeat} required type="password" icon={keyIcon} label="Confirm Password" placeholder="Enter your password again" />
            <div className="actions">
              <Button disabled={!passwordsMatch} onClick={updateUserPassword} isLoading={/*isUpdatingPassword*/false}>Update Password</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getTransactionData = () => {
    return userTransactions.map((t:IUserTransactionSummary, i: number) => {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: t.amounts.currency.toUpperCase() || "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      const row = {
        created_at: moment(t.created_at).format("MMMM Do, YYYY  h:mm a"),
        status: <span className={"status " + t.status}>{t.status}</span>,
        amount: formatter.format(t.amounts.processed_total_amount ? t.amounts.processed_total_amount / 100 : t.amounts.calculated_total_amount / 100),
        event_name: <Link to={"/e/" + t.event.handle}>{t.event.title}</Link>,
        actions: t.status === "success" ? [
          {
            icon: "download",
            onClick: () => {
              if (t) {
                download.pushDownload({
                  type: "transaction",
                  transactionId: t._id
                });
              }
            }
          }
        ] : []
      }

      return row;
    });
  }

  const openDogForm = (dog?: IDog, tab = 0) => {
    setCurrentDog(dog);
    setDogFormTab(tab);
    setShowDogModal(true);
  };

  const closeDogForm = () => {
    setShowDogModal(false);
    setCurrentDog(undefined);
    setDogFormTab(0);
  };

  const openDog = (dog: IDog) => {
    navigate(hasActiveSubscription ? `/dogs/${dog._id}` : "/premium");
  };

  const renderDogs = () => {
    return (
      <div className="dogsPanel">
        {!hasActiveSubscription && (
          <div className="upgradeBanner">
            <div className="upgradeBannerIcon">🐾</div>
            <div className="upgradeBannerContent">
              <div className="upgradeBannerTitle">Unlock Your Full Potential with Premium!</div>
              <div className="upgradeBannerText">
                Unlock enhanced dog profiles, track your titles, view event results, and share your progress!
              </div>
            </div>
            <Button className="upgradeBannerButton" onClick={() => navigate("/premium")}>Upgrade to Premium</Button>
          </div>
        )}
        <DogTiles
          dogs={dogs}
          hasProfileAccess={hasActiveSubscription}
          onView={openDog}
          onEdit={(dog) => openDogForm(dog)}
          onAddPhoto={(dog) => openDogForm(dog, DOG_FORM_IMAGE_TAB)}
          onArchive={(dog) => { setCurrentDog(dog); toggle(); }}
        />
      </div>
    );
  }

  const renderSubscriptionPlan = () => {
    return (
      <div className="settingsTab">
        <UserSubscriptionTab/>
      </div>
    )
  }

  const renderTransactions = () => {
    return <UserTransactionsTab userId={user._id} />;
  }

  const renderOwnerResults = () => {
    return <UserScoresTab dogs={dogs} />;
  }

  const renderMyClubs = () => {
    return <MyClubsTab />;
  }

  // New render functions for club manager reorganized tabs
  const renderBasicInformation = () => {
    return (
      <div className="settingsTab">
        <div className="description">
          <div className="title">Basic Information</div>
          <div className="text">Manage your club's essential details and contact information</div>
          {!user?.is_email_verified &&
            <div className="accountWarning">
              <div className="iconWrapWarning"><div className="icon alert"></div></div>
              <div className="details">
                <span>Your email isn't verified, this may hinder some functionality on Eagerdog. <Link to="/verify-email?r=/account&sendOnLoad=1">Verify Email</Link></span>
              </div>
            </div>
          }
        </div>
        <form className="inputs" onSubmit={(e) => { e.preventDefault(); updateUserInfo(); }}>
          <div className="inputRow">
            <Input required label="Account Name" id="displayName" type="text" onChange={(e) => { e.stopPropagation(); setDisplayName(e.target.value); setUserInfoChanged(true); }} defaultValue={displayName} placeholder="Update your clubs name" />
          </div>
          <div className="inputRow">
            <Phone label="Phone Number" value={phone} onChange={(e: any) => { setPhone(e); setUserInfoChanged(true); }} />
          </div>
          <div className="inputRow">            
            <div className={!user.is_email_verified ? "emailWrap unverified" : "emailWrap"}><Input disabled required label="Email" onChange={(e:any) => {  }} defaultValue={user?.email} type="email" placeholder="Enter your email" />{!user?.is_email_verified && <div className="iconWrap"><div className="icon alert"></div></div>}</div>
          </div>
          <div className="inputRow">
            <Input label="Website" id="website" type="text" onChange={(e) => { e.stopPropagation(); setWebsite(e.target.value); setUserInfoChanged(true); }} defaultValue={user?.website} placeholder="Update your website" />            
          </div>
          <div className="inputRow">            
            <Input label="Club ID" id="clubID" type="text" onChange={(e) => { e.stopPropagation(); setClubID(e.target.value); setUserInfoChanged(true); }} defaultValue={clubID} placeholder="Club ID" />
          </div>
          <div className="actions">
            <Button type="submit" disabled={!userInfoChanged} isLoading={isUpdatingPersonalInfo}>Save</Button>
          </div>
        </form>
      </div>
    );
  }

  const renderLocation = () => {
    return (
      <div className="settingsTab">
        <div className="description">
          <div className="title">Location</div>
          <div className="text">Set your club's address and location information</div>
        </div>
        <form className="inputs" onSubmit={(e) => { e.preventDefault(); updateUserInfo(); }}>
          <div className="inputRow">
            <Input label="Address Line 1" id="line1" type="text" onChange={(e) => { e.stopPropagation(); setLine1(e.target.value); setUserInfoChanged(true); }} defaultValue={line1} placeholder="Address Line 1" />
          </div>
          <div className="inputRow">
            <Input label="Line 2" id="line2" type="text" onChange={(e) => { e.stopPropagation(); setLine2(e.target.value); setUserInfoChanged(true); }} defaultValue={line2} placeholder="Line 2" />
          </div>
          <div className="inputRow">
            <Input label="City" id="city" type="text" onChange={(e) => { e.stopPropagation(); setCity(e.target.value); setUserInfoChanged(true); }} defaultValue={city} placeholder="City" /> 
          </div>
          <div className="inputRow">            
            <Dropdown value={state} onChange={(e: any, value: IOption) => { setState(value); setUserInfoChanged(true); }} icon={flagIcon} label="Province / State" options={country.value === "USA" ? States : Provinces} placeholder="Choose your state/province of residence" />
          </div>
          <div className="inputRow">
            <Dropdown value={country} onChange={(e: any, value: IOption) => { setCountry(value); }} icon={flagIcon} label="Country" options={[{ value: "Canada", id: "Canada" }, { value: "USA", id: "USA" }]} placeholder="Choose your country of residence" />
          </div>
          <div className="inputRow">
            <Input label="Zip" id="postalCode" type="text" onChange={(e) => { e.stopPropagation(); setPostalCode(e.target.value); setUserInfoChanged(true); }} defaultValue={postalCode} placeholder="Postal Code" />
          </div>
          <div className="actions">
            <Button type="submit" disabled={!userInfoChanged} isLoading={isUpdatingPersonalInfo}>Update Location</Button>
          </div>
          <div className="inputRow">
            <div className="mapSection">
              <div className="mapLabel">Map Location</div>
              <div className="mapDescription">Automatically updates based on the address above</div>
              <div className="mapWrapper">
                {<GoogleMapComponent
                  club={{
                    _id: user._id,
                    club_name: displayName,
                    email: user.email,
                    phone: phone,
                    address: {
                      line1,
                      line2,
                      city,
                      state: state.id,
                      country: country.id,
                      postal_code: postalCode
                    },
                    coordinates: mapCoordinates
                  }}
                  readonly={true}
                />}
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  }

  const renderCustomization = () => {
    if (!currentClub) {
      return (
        <div className="settingsTab">
          <div className="description">
            <div className="title">Customization</div>
            <div className="text">Loading club information...</div>
          </div>
        </div>
      );
    }

    return (
      <div className="settingsTab">
        <div className="description">
          <div className="title">Customization</div>
          <div className="text">Customize your club's public page with logo, cover image, and description.</div>
        </div>
        <div className="inputs">
          <ClubSettings 
            club={currentClub} 
            onClubUpdate={(updatedClub) => {
              setCurrentClub(updatedClub);
            }} 
          />
        </div>
      </div>
    );
  }

  const isClubManager = user.user_type === Constants.user_type.club_manager;
  const pageLabel = tabs?.[activeTab]?.name || "Settings";
  const isTilePage = ["My Dogs", "My Clubs", "Entries", "Results", "Transactions"].includes(pageLabel);
  const isOpenPage = ["Entries", "Results", "Transactions"].includes(pageLabel);
  const accountTitle = isClubManager
    ? (currentClub?.club_name || user.display_name)
    : (user.display_name || [user.first_name, user.last_name].filter(Boolean).join(" ") || "Account");
  const accountAddress = isClubManager ? currentClub?.address : user.address;
  const accountLocation = [accountAddress?.city, accountAddress?.state, accountAddress?.country].filter(Boolean).join(", ");

  return (
    <div className={`Account ManagePage${isTilePage ? " tilePage" : ""}${isOpenPage ? " entriesPage" : ""}`}>
      <Helmet>
        <title>{pageLabel}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <SectionSidebar
        storageKey="eagerdog.accountSidebarCollapsed"
        menuLabel="Account menu"
        eyebrow={accountTitle}
        header={{
          title: accountTitle,
          subtitle: accountLocation,
          imageUrl: isClubManager ? currentClub?.logo_url : undefined,
          imageAlt: accountTitle,
          fallbackName: accountTitle,
          linkTo: isClubManager && currentClub?._id ? `/k/${currentClub._id}` : undefined,
          linkLabel: isClubManager && currentClub?._id ? "View Club" : undefined,
        }}
        groups={sectionsFromTabs(tabs || [], isClubManager ? MANAGER_SECTIONS : OWNER_SECTIONS)}
        activeId={String(activeTab)}
        onSelect={(id) => setTab(Number(id))}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <div className="accountMain">
      <div className="accountInner">
        <div className="pageHeader">
          <div className="breadcrumb"><div className="path"><span>Profile</span> /</div><div className="current">{pageLabel}</div></div>
          <SectionMenuButton label="Account menu" onClick={() => setMobileNavOpen(true)} />
        </div>
        <div className="pageTitleRow">
          <div>
            <h1>{pageLabel}</h1>
            {!isClubManager && pageLabel === "My Dogs" && (
              <p className="pageSubtext">Keep profiles current so registration details are always ready for your next event.</p>
            )}
            {pageLabel === "Personal Information" && (
              <p className="pageSubtext">Keep your personal information up-to-date and secure with your personal information settings.</p>
            )}
            {pageLabel === "My Clubs" && (
              <p className="pageSubtext">Manage your favorite clubs and choose which clubs can send you direct communications.</p>
            )}
            {pageLabel === "Advanced" && (
              <p className="pageSubtext">Choose how checkout reminds you about club points.</p>
            )}
          </div>
          {!isClubManager && pageLabel === "My Dogs" && (
            <Button className="addDogDesktop" onClick={() => openDogForm()}>Add dog</Button>
          )}
        </div>
        {!isClubManager && pageLabel === "My Dogs" && (
          <button type="button" className="addDogMobile" onClick={() => openDogForm()}>
            <span>Add dog</span>
          </button>
        )}
        {user.user_type === "owner" ? renderOwnerTabs() : renderManagerTabs()}
      </div>
      </div>
      <Modal
        className={"dogForm"}
        isShown={showDogModal}
        hide={closeDogForm}
        modalContent={<DogForm initialTab={dogFormTab} onFinish={() => { getDogs(); closeDogForm(); }} onDogUpdated={(updated) => setCurrentDog(updated)} dog={currentDog} />}
      />
      <Modal className="Confirmation" isShown={isShown} hide={toggle} modalContent={
        <Confirm 
          message={<><p>Are you sure you want to archive this dog?</p></>}
          onConfirm={handleConfirm} 
          onCancel={handleCancel} 
        />
      } />
      <Modal 
        isShown={showWelcomeModal} 
        hide={() => setShowWelcomeModal(false)}
        className="congratsModal"
        modalContent={
          <Congrats
            title="Welcome to Eager Dog! 🐾"
            text="Your account has been created successfully. Join our Facebook Community Group to connect with other dog owners and clubs, ask questions, and stay up to date on new features and updates."
            urlText="Join Facebook Group"
            url="https://www.facebook.com/groups/eagerdog"
          />
        }
      />
    </div>
  );
};

export default Account;